import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import "./DepositForm.css";
import "./View.css";

const View = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [depositForm, setDepositForm] = useState(null);
  const [depositSections, setDepositSections] = useState([]);
  const [depositSubSections, setDepositSubSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Dynamic time slots based on shift
  const getTimeSlots = (shiftType) => {
    if (shiftType === "Day Shift") {
      return [
        "5:00 am",
        "6:00 am",
        "7:00 am",
        "8:00 am", 
        "9:00 am",
        "10:00 am",
        "11:00 am",
        "12:00 pm",
        "1:00 pm",
        "2:00 pm",
        "3:00 pm",
        "4:00 pm",
        "Closing",
        "Total"
      ];
    } else { // Night Shift
      return [
        "4:00 pm",
        "5:00 pm",
        "6:00 pm",
        "7:00 pm",
        "8:00 pm",
        "9:00 pm",
        "10:00 pm",
        "11:00 pm",
        "12:00 am",
        "1:00 am",
        "2:00 am",
        "3:00 am",
        "4:00 am",
        "5:00 am",
        "Closing",
        "Total"
      ];
    }
  };

  // Static columns that appear after dynamic columns
  const staticColumns = [
    // "W. Sales",
    // "Diposite", 
    // "Short",
    "Bus Staff",
    // "Total",
    "Grant Total"
  ];

  // Fetch deposit form + full structure (sections & subsections)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [sectionsResponse, subSectionsResponse, formResponse] = await Promise.all([
          Http.get("/accounts/deposit-section"),
          Http.get("/accounts/deposit-sub-section"),
          Http.get(`/accounts/deposit-forms/${id}`)
        ]);

        setDepositSections(sectionsResponse?.data || []);
        setDepositSubSections(subSectionsResponse?.data || []);

        if (formResponse?.data?.success) {
          const formData = formResponse.data.data;
          setDepositForm(formData);
        } else {
          setError("Failed to load deposit form data");
        }
      } catch (error) {
        console.error("Error fetching deposit form:", error);
        setError("Error loading deposit form data");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Group subsections by section
  const getSectionColumns = () => {
    const columns = [];
    depositSections.forEach(section => {
      const sectionSubSections = depositSubSections.filter(sub => sub.deposit_section_id === section.id);
      if (sectionSubSections.length > 0) {
        columns.push({
          type: 'section',
          section: section,
          subSections: sectionSubSections
        });
      } else {
        columns.push({
          type: 'section',
          section: section,
          subSections: []
        });
      }
    });
    return columns;
  };

  // Format display: show '' if value is 0 or missing, otherwise show 2 decimal places
  const formatCellValue = (val) => {
    if (val === null || val === undefined) return '';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '';
    return num === 0 ? '' : num.toFixed(2);
  };

  // Get value for specific item, time slot, and column
  const getItemValue = (timeSlot, columnKey) => {
    // Handle "Total" row specially
    if (timeSlot === "Total") {
      if (depositForm?.form_data && depositForm.form_data["Total"]) {
        return formatCellValue(depositForm.form_data["Total"][columnKey]);
      }
      if (!depositForm?.items || depositForm.items.length === 0) return '';
      const sum = depositForm.items
        .filter(i => i.column_key === columnKey && i.time_slot !== "Total")
        .reduce((acc, i) => acc + (parseFloat(i.value) || 0), 0);
      return sum === 0 ? '' : sum;
    }

    // Prefer form_data when available
    if (depositForm?.form_data && depositForm.form_data[timeSlot]) {
      return formatCellValue(depositForm.form_data[timeSlot][columnKey]);
    }

    // Fallback to items array
    if (!depositForm?.items || depositForm.items.length === 0) return '';
    const item = depositForm.items.find(i => i.time_slot === timeSlot && i.column_key === columnKey);
    const value = item ? (parseFloat(item.value) || 0) : 0;
    return value === 0 ? '' : value;
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="deposit-form-container">
        <IsLogin />
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title deposit-form-title">View Daily Collection</h5>
          </div>
          <div className="am-pagebody">
            <div className="card deposit-form-card pd-20 pd-sm-40">
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading form data...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate("/daily-collection")}
                >
                  Back to List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!depositForm) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-warning" role="alert">
                  Deposit form not found
                </div>
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate("/daily-collection")}
                >
                  Back to List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const timeSlots = getTimeSlots(depositForm.shift);
  const sectionColumns = getSectionColumns();

  return (
    <div className="deposit-form-container">
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title deposit-form-title">View Daily Collection</h5>
        </div>
        <div className="am-pagebody">
          <div className="card deposit-form-card pd-20 pd-sm-40">
            {/* Header with Print Button */}
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
              <h4 className="card-title mb-0">Daily Collection Details</h4>
              <div>
                <button 
                  className="btn btn-success me-2"
                  onClick={handlePrint}
                >
                  <i className="fa fa-print"></i> Print
                </button>
                <button 
                  className="btn btn-primary me-2"
                  onClick={() => navigate(`/edit-daily-collection/${id}`)}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate("/daily-collection")}
                >
                  Back to List
                </button>
              </div>
            </div>

            {/* Form Info */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label"><strong>Date:</strong></label>
                  <p className="form-control-plaintext">
                    {depositForm.date ? new Date(depositForm.date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label"><strong>Shift:</strong></label>
                  <p className="form-control-plaintext">
                    {depositForm.shift || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Dynamic Table */}
            <div className="table-responsive">
              <table className="table table-bordered table-striped deposit-table">
                <thead>
                  <tr>
                    <th rowSpan="2" className="align-middle time-cell">Time</th>

                    {/* Dynamic section headers */}
                    {sectionColumns.map((column, index) => (
                      <th 
                        key={`section-${column.section.id}`}
                        colSpan={column.subSections.length || 1}
                        className="text-center section-header"
                      >
                        Section- {column.section.name}
                      </th>
                    ))}

                    {/* Static column headers */}
                    {staticColumns.map((column, index) => (
                      <th 
                        key={`static-${index}`}
                        rowSpan="2" 
                        className="align-middle text-center static-header"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>

                  <tr>
                    {/* Dynamic subsection headers */}
                    {sectionColumns.map((column) => 
                      column.subSections.length > 0 
                        ? column.subSections.map((subSection) => (
                            <th 
                              key={`subsection-${subSection.id}`}
                              className="text-center subsection-header"
                            >
                              {subSection.name}
                            </th>
                          ))
                        : (
                            <th 
                              key={`section-only-${column.section.id}`}
                              className="text-center subsection-header"
                            >
                              {column.section.name}
                            </th>
                          )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {timeSlots.map((timeSlot, timeIndex) => (
                    <tr key={timeSlot} className={timeSlot === "Total" ? "total-row" : ""}>
                      <td className="time-cell">{timeSlot}</td>

                      {/* Dynamic columns */}
                      {sectionColumns.map((column) => 
                        column.subSections.length > 0 
                          ? column.subSections.map((subSection) => {
                              const columnKey = `section_${column.section.id}_subsection_${subSection.id}`;
                              return (
                                <td key={columnKey} className="text-center">
                                  <span className="form-control-plaintext text-center d-block">
                                    {getItemValue(timeSlot, columnKey)}
                                  </span>
                                </td>
                              );
                            })
                          : (
                              <td key={`section-only-${column.section.id}`} className="text-center">
                                <span className="form-control-plaintext text-center d-block">
                                  {getItemValue(timeSlot, `section_${column.section.id}`)}
                                </span>
                              </td>
                            )
                      )}

                      {/* Static columns */}
                      {staticColumns.map((column, index) => {
                        let columnKey;
                        switch(column) {
                          case "W. Sales":
                            columnKey = "w._sales";
                            break;
                          case "Diposite":
                            columnKey = "diposite";
                            break;
                          case "Short":
                            columnKey = "short";
                            break;
                          case "Bus Staff":
                            columnKey = "bus_staff";
                            break;
                          case "Total":
                            columnKey = "total";
                            break;
                          case "Grant Total":
                            columnKey = "grant_total";
                            break;
                          default:
                            columnKey = column.toLowerCase().replace(/\s+/g, '_');
                        }

                        return (
                          <td key={`static-${index}`} className="text-center">
                            <span className="form-control-plaintext text-center d-block">
                              {getItemValue(timeSlot, columnKey)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Additional Info */}
            {depositForm.notes && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label"><strong>Notes:</strong></label>
                    <p className="form-control-plaintext">
                      {depositForm.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="row mt-4 no-print">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label"><strong>Created At:</strong></label>
                  <p className="form-control-plaintext">
                    {depositForm.created_at ? new Date(depositForm.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label"><strong>Last Updated:</strong></label>
                  <p className="form-control-plaintext">
                    {depositForm.updated_at ? new Date(depositForm.updated_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default View;